/**
 * Leaguemate Scouting — S3 deep-dive transaction fetch (Tier 2, on click).
 *
 * Expensive work runs ONLY when the user selects one owner (§3.0). For that
 * owner we pull /transactions/{w} across their dynasty/keeper TX set (capped),
 * reusing S1's scoutFetch (throttle + IndexedDB cache + 429 backoff). Weeks are
 * scanned 0..18 and short-circuited after DEEP_TX_EMPTY_WEEK_STREAK consecutive
 * empty weeks (the same heuristic fetchSeasonPayload uses for matchups).
 *
 * Transaction shape confirmed against real cached JSON (scout-audit-cache):
 *   trade : { type:"trade", status, creator(uid), roster_ids[], consenter_ids[],
 *             adds{pid:rid}, drops{pid:rid}, draft_picks[], waiver_budget[], created }
 *   waiver: { type:"waiver", status, roster_ids[], adds{pid:rid},
 *             settings:{waiver_bid}, created }
 */

import { scoutFetch, SCOUT_BASE_URL } from "./scoutFetch.js";
import {
  DYNASTY_KEEPER_TYPES,
  DEEP_TX_LEAGUES,
  DEEP_TX_MAX_WEEK,
  DEEP_TX_EMPTY_WEEK_STREAK,
  ROOKIE_DRAFT_MAX_ROUNDS,
} from "./scoutConfig.js";

const noop = () => {};
const DK_TYPES = new Set(DYNASTY_KEEPER_TYPES);

/**
 * Select a manager's transaction-set from their S2 affinity entries: dynasty/
 * keeper only, non-best-ball, with a known roster_id, sorted by similarity desc
 * (entries already arrive ranked), capped to DEEP_TX_LEAGUES. This REFINES S2's
 * similarity-only `inTxSet` flag to honor the §3.0.1 inclusion policy.
 */
export function selectTxCandidateSet(affinityEntries, opts = {}) {
  const cap = opts.cap ?? DEEP_TX_LEAGUES;
  return (affinityEntries || [])
    .filter(
      (e) =>
        e.roster_id != null &&
        !e.bestBall &&
        DK_TYPES.has(e.leagueType)
    )
    .sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0))
    .slice(0, cap);
}

/**
 * Pull complete transactions for one league, short-circuiting empty weeks.
 * Returns the league's complete transactions (all types — pure layers filter).
 */
async function fetchLeagueTransactions(leagueId, season, onProgress) {
  const complete = [];
  let emptyStreak = 0;
  for (let w = 0; w <= DEEP_TX_MAX_WEEK; w++) {
    const wk = await scoutFetch(`${SCOUT_BASE_URL}/league/${leagueId}/transactions/${w}`, {
      cacheId: leagueId, season, dataType: `scout_tx_${w}`,
    });
    const arr = Array.isArray(wk) ? wk : [];
    if (arr.length === 0) {
      if (++emptyStreak >= DEEP_TX_EMPTY_WEEK_STREAK) break;
      continue;
    }
    emptyStreak = 0;
    for (const t of arr) if (t && t.status === "complete") complete.push(t);
    onProgress(`tx ${leagueId} wk ${w}`, w, DEEP_TX_MAX_WEEK);
  }
  return complete;
}

/**
 * Pull complete transactions for a pre-selected TX set (one league-row per
 * entry). Carries the roster `players` (already cached from S2) so the rebuild/
 * positional roster signals need no refetch.
 * Returns per-league: { league_id, league_name, season, similarity, leagueType,
 * roster_id, faabBudget, players[], completeTx[] }.
 */
export async function fetchTransactionsForSet(txSet, { onProgress = noop } = {}) {
  return Promise.all(
    (txSet || []).map(async (e, i) => {
      const completeTx = await fetchLeagueTransactions(e.league_id, e.season, onProgress);
      onProgress(`Pulled transactions ${i + 1}/${txSet.length}`, i + 1, txSet.length);
      return {
        league_id:   e.league_id,
        league_name: e.league_name,
        season:      e.season,
        similarity:  e.similarity,
        leagueType:  e.leagueType,
        roster_id:   e.roster_id,
        faabBudget:  e.waiverBudget || 0,
        players:     e.players || [],
        completeTx,
      };
    })
  );
}

/** Convenience wrapper: select the TX set then pull transactions for it. */
export async function fetchManagerTransactions(affinityEntries, opts = {}) {
  return fetchTransactionsForSet(selectTxCandidateSet(affinityEntries), opts);
}

/**
 * Pull picks/drafts for a pre-selected TX set (S3.5). Per league:
 *   /league/{id}/traded_picks  → future-pick ownership (rebuild intent)
 *   /league/{id}/drafts        → identify rookie drafts (settings.rounds ≤ N)
 *   /draft/{id}/picks          → the manager's rookie picks (positional bias)
 * Returns per-league: { league_id, season, roster_id, tradedPicks[], rookiePicks[] }
 * where rookiePicks are THIS manager's picks ({ round, position }).
 */
export async function fetchPicksDraftsForSet(txSet, { onProgress = noop } = {}) {
  return Promise.all(
    (txSet || []).map(async (e, i) => {
      const rid = e.roster_id;
      const [tradedPicks, drafts] = await Promise.all([
        scoutFetch(`${SCOUT_BASE_URL}/league/${e.league_id}/traded_picks`, {
          cacheId: e.league_id, season: e.season, dataType: "scout_traded_picks",
        }),
        scoutFetch(`${SCOUT_BASE_URL}/league/${e.league_id}/drafts`, {
          cacheId: e.league_id, season: e.season, dataType: "scout_drafts",
        }),
      ]);

      const rookieDrafts = (Array.isArray(drafts) ? drafts : []).filter(
        (d) => (d.settings || {}).rounds != null && d.settings.rounds <= ROOKIE_DRAFT_MAX_ROUNDS
      );
      const rookiePicks = [];
      for (const d of rookieDrafts) {
        const picks = await scoutFetch(`${SCOUT_BASE_URL}/draft/${d.draft_id}/picks`, {
          cacheId: d.draft_id, season: d.season || e.season, dataType: "scout_draft_picks",
        });
        for (const p of Array.isArray(picks) ? picks : []) {
          if (p.roster_id === rid) rookiePicks.push({ round: p.round, position: (p.metadata || {}).position || null });
        }
      }

      onProgress(`Pulled picks/drafts ${i + 1}/${txSet.length}`, i + 1, txSet.length);
      return {
        league_id: e.league_id,
        season: e.season,
        roster_id: rid,
        tradedPicks: Array.isArray(tradedPicks) ? tradedPicks : [],
        rookiePicks,
      };
    })
  );
}
